import React from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Mail, Phone, Globe, Linkedin, BadgeCheck, Brain, Cloud, Database, Bot, Sparkles, GraduationCap, Languages, Award, CheckCircle2 } from 'lucide-react';

export const AboutIntro: React.FC = () => {
  const { i18n } = useTranslation();
  const isIt = i18n.language === 'it';

  const data = isIt
    ? {
        name: 'Victor Lazari',
        title: 'Libero Professionista IT & System Architect',
        initiative: 'Iniziativa personale: Varienda-BMS – Business Management Systems',
        location: 'Ravenna, Emilia-Romagna',
        email: 'victor@victorlazari.com',
        phone: '+39 380 633 1704',
        website: 'victorlazari.com',
        linkedin: 'linkedin.com/in/victor-lazari',
        profile:
          'Consulente IT indipendente specializzato in sviluppo web, CRM e automazioni intelligenti per PMI. Con Varienda-BMS progetto sistemi digitali integrati che combinano visibilità online, gestione clienti e analisi operativa. Obiettivo: trasformare la tecnologia in un vantaggio competitivo reale e misurabile.',
        skills: [
          { icon: <Brain className="w-4 h-4 text-violet-600 dark:text-violet-400" />, label: 'Sviluppo & Automazione: React · Node.js · Supabase · n8n · TypeScript · Python' },
          { icon: <Cloud className="w-4 h-4 text-violet-600 dark:text-violet-400" />, label: 'Cloud & Integrazioni: Azure App Service · REST API · WhatsApp Business' },
          { icon: <Database className="w-4 h-4 text-violet-600 dark:text-violet-400" />, label: 'CRM & Dati: Supabase CRM · Google Business API · PowerApps' },
          { icon: <Bot className="w-4 h-4 text-violet-600 dark:text-violet-400" />, label: 'AI & Automation: OpenAI API · Automazioni agentiche · Workflow intelligenti' },
          { icon: <Sparkles className="w-4 h-4 text-violet-600 dark:text-violet-400" />, label: 'Soft Skills: Analisi processi · Comunicazione con PMI · Project Design · Innovazione pragmatica' }
        ],
        experience: [
          'Libero Professionista IT – Iniziativa Varienda-BMS (Ravenna, 2024 – Oggi): soluzioni end‑to‑end per la digitalizzazione di PMI (siti, CRM, automazioni WhatsApp, dashboard).',
          'Varienda-BMS: architettura modulare “IT‑as‑a‑Service” per PMI. Stack: React + Supabase + n8n + Azure.',
          'AV Infissi – av-infissi.it: sito SEO + flussi preventivo e CRM WhatsApp. +60% contatti qualificati in 3 mesi.',
          'La Falegnameria SAS – lafalegnameria-sas.it: digitalizzazione completa con CRM PowerApps e dashboard PowerBI.',
          'Salugea S.r.l.: dashboard operative e personalizzazione PowerBI per reparti aziendali.',
          'Vector Workflow Automation: piattaforma approvazioni multi‑tenant con logica agentica AI (React, NestJS, Supabase, n8n, Azure).'
        ],
        education: 'Laurea in Informatica — Università di Modena e Reggio Emilia (2020). Certificazioni: Microsoft AZ‑900 · Power Platform Essentials',
        languages: [
          'Italiano — Madrelingua',
          'Romeno — Madrelingua',
          'Russo — Madrelingua',
          'Inglese — Professionale'
        ],
        outcomes: [
          '20+ progetti di digitalizzazione (2023–2025)',
          '−40% tempi di gestione clienti',
          '+35% lead medi',
          'Implementazioni full‑stack su Azure, Supabase, n8n'
        ],
        references: [
          'Salugea S.r.l.: “Le dashboard create da Victor sono operative e disponibili per tutte le funzioni aziendali.”',
          'La Falegnameria SAS: “Victor ha compreso le nostre esigenze e realizzato soluzioni oltre le aspettative.”'
        ],
      }
    : {
        name: 'Victor Lazari',
        title: 'Independent IT Consultant & System Architect',
        initiative: 'Personal initiative: Varienda‑BMS – Business Management Systems',
        location: 'Ravenna, Emilia‑Romagna',
        email: 'victor@victorlazari.com',
        phone: '+39 380 633 1704',
        website: 'victorlazari.com',
        linkedin: 'linkedin.com/in/victor-lazari',
        profile:
          'Independent IT consultant specialized in web development, CRM and intelligent automations for SMEs. Through Varienda‑BMS I design integrated digital systems combining online visibility, client management and operational analytics. The goal: turn technology into a real, measurable competitive advantage.',
        skills: [
          { icon: <Brain className="w-4 h-4 text-violet-600 dark:text-violet-400" />, label: 'Development & Automation: React · Node.js · Supabase · n8n · TypeScript · Python' },
          { icon: <Cloud className="w-4 h-4 text-violet-600 dark:text-violet-400" />, label: 'Cloud & Integrations: Azure App Service · REST APIs · WhatsApp Business' },
          { icon: <Database className="w-4 h-4 text-violet-600 dark:text-violet-400" />, label: 'CRM & Data: Supabase CRM · Google Business API · PowerApps' },
          { icon: <Bot className="w-4 h-4 text-violet-600 dark:text-violet-400" />, label: 'AI & Automation: OpenAI API · Agentic automations · Intelligent workflows' },
          { icon: <Sparkles className="w-4 h-4 text-violet-600 dark:text-violet-400" />, label: 'Soft skills: Process analysis · SME communication · Project design · Pragmatic innovation' }
        ],
        experience: [
          'Independent IT – Varienda‑BMS (Ravenna, 2024 – Today): end‑to‑end solutions for SME digitization (sites, CRM, WhatsApp automations, dashboards).',
          'Varienda‑BMS: modular “IT‑as‑a‑Service” architecture for SMEs. Stack: React + Supabase + n8n + Azure.',
          'AV Infissi – av‑infissi.it: SEO site + quote flows and WhatsApp CRM. +60% qualified leads in 3 months.',
          'La Falegnameria SAS – lafalegnameria‑sas.it: full digitization with PowerApps CRM and PowerBI dashboards.',
          'Salugea S.r.l.: operational dashboards and PowerBI customization for all departments.',
          'Vector Workflow Automation: multi‑tenant approvals platform with agentic AI (React, NestJS, Supabase, n8n, Azure).'
        ],
        education: 'BSc in Computer Science — University of Modena and Reggio Emilia (2020). Certifications: Microsoft AZ‑900 · Power Platform Essentials',
        languages: [
          'Italian — Native',
          'Romanian — Native',
          'Russian — Native',
          'English — Professional'
        ],
        outcomes: [
          '20+ SME digitization projects (2023–2025)',
          '−40% client handling time',
          '+35% average leads',
          'Full‑stack implementations on Azure, Supabase, n8n'
        ],
        references: [
          'Salugea S.r.l.: “Victor’s dashboards are operational and available to all business functions.”',
          'La Falegnameria SAS: “Victor understood our needs and delivered beyond expectations.”'
        ],
      };

  return (
    <section className="relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 md:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-gray-100">{data.name}</h2>
              <p className="text-slate-700 dark:text-gray-300 mt-1">{data.title}</p>
              <p className="text-slate-500 dark:text-gray-400 text-sm mt-1">{data.initiative}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700 dark:text-gray-300">
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-violet-600 dark:text-violet-400" /> {data.location}</div>
              <a href={`mailto:${data.email}`} className="flex items-center gap-2 hover:text-slate-900 dark:hover:text-gray-100"><Mail className="w-4 h-4 text-violet-600 dark:text-violet-400" /> {data.email}</a>
              <a href={`tel:${data.phone.replace(/\s/g,'')}`} className="flex items-center gap-2 hover:text-slate-900 dark:hover:text-gray-100"><Phone className="w-4 h-4 text-violet-600 dark:text-violet-400" /> {data.phone}</a>
              <a href={`https://${data.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-slate-900 dark:hover:text-gray-100"><Globe className="w-4 h-4 text-violet-600 dark:text-violet-400" /> {data.website}</a>
              <a href={`https://${data.linkedin}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-slate-900 dark:hover:text-gray-100"><Linkedin className="w-4 h-4 text-violet-600 dark:text-violet-400" /> {data.linkedin}</a>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <p className="text-slate-800 dark:text-gray-200 leading-relaxed text-base">{data.profile}</p>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.skills.map((s, i) => (
                  <div key={i} className="flex items-start gap-2 rounded-md border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-700/50 p-3 text-sm text-slate-700 dark:text-gray-300">
                    {s.icon}
                    <span>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              <div className="rounded-md border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-700/50 p-3">
                <p className="text-xs font-semibold text-slate-500 dark:text-gray-400 mb-2">{isIt ? 'Lingue' : 'Languages'}</p>
                <ul className="space-y-1 text-sm text-slate-700 dark:text-gray-300">
                  {data.languages.map((l, i) => (
                    <li key={i} className="flex items-start gap-2"><Languages className="w-4 h-4 text-violet-600 dark:text-violet-400 mt-0.5" /> {l}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 lg:col-span-2">
              <p className="text-xs font-semibold text-slate-500 dark:text-gray-400 mb-2">{isIt ? 'Esperienza selezionata' : 'Selected experience'}</p>
              <div className="grid md:grid-cols-2 gap-3">
                {data.experience.slice(0,4).map((e, i) => (
                  <div key={i} className="rounded-md border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-700/50 p-3 text-sm text-slate-700 dark:text-gray-300 flex items-start gap-2">
                    <BadgeCheck className="w-4 h-4 text-violet-600 dark:text-violet-400 mt-0.5" />
                    <span>{e}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
              <p className="text-xs font-semibold text-slate-500 dark:text-gray-400 mb-2">{isIt ? 'Risultati chiave' : 'Key results'}</p>
              <ul className="space-y-2 text-sm text-slate-700 dark:text-gray-300">
                {data.outcomes.map((o, i) => (
                  <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-violet-600 dark:text-violet-400 mt-0.5" /> {o}</li>
                ))}
              </ul>
              <div className="mt-4 p-3 rounded-md border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-700/50">
                <p className="text-xs font-semibold text-slate-500 dark:text-gray-400 mb-2">{isIt ? 'Referenze' : 'References'}</p>
                <ul className="space-y-2 text-sm text-slate-700 dark:text-gray-300">
                  {data.references.map((r, i) => (
                    <li key={i} className="flex items-start gap-2"><Award className="w-4 h-4 text-violet-600 dark:text-violet-400 mt-0.5" /> {r}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutIntro;


